import React, { useRef, useState } from "react";
import { Table, Input, Button, Space, InputRef } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import type { ColumnType, ColumnsType } from "antd/es/table";
import type { FilterConfirmProps } from "antd/es/table/interface";
import useSWR from "swr";
import "./location_rack_picking_return.css";
import axios from "axios";

const backendUrl = process.env.REACT_APP_BACKEND_URL;
export interface RackLocationRecord {
    id: number;
    code_rack: string;
    warehouse_code?: string;
}

interface LocationRackPickingReturnProps {
    warehouse?: string;
    warehouseCode?: string;
    itemCode?: string;
    onSelectLocation?: (record: RackLocationRecord) => void;
}

type DataIndex = keyof RackLocationRecord;

const fetcher = (url: string) => axios.get(url).then((res) => res.data);

const LocationRackPickingReturn: React.FC<LocationRackPickingReturnProps> = ({ warehouse, warehouseCode, itemCode, onSelectLocation }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState<DataIndex | ''>('');
    const searchInput = useRef<InputRef>(null);
    const params = new URLSearchParams(); 
    if (itemCode) params.append('item_code', itemCode.trim()); 
    if (warehouseCode) params.append('warehouse_code', warehouseCode.trim()); 
    else if (warehouse) params.append('warehouse', warehouse.trim()); 
    const url = `${backendUrl || ''}/api/spareparts-rack-locations${params.toString() ? `?${params.toString()}` : ''}`;
    const { data: rackData, isLoading } = useSWR<RackLocationRecord[]>(url, fetcher);
    const normalizedWarehouseCode = (warehouseCode || '').trim().toLowerCase(); 
    const filteredRackData = React.useMemo(() => (Array.isArray(rackData) ? rackData : []).filter((rack) => !normalizedWarehouseCode || (rack.warehouse_code || '').trim().toLowerCase() === normalizedWarehouseCode), [rackData, normalizedWarehouseCode]); 
    React.useEffect(() => { 
        console.log("[LocationRackPickingReturn] Warehouse Filter:", warehouse); 
        console.log("[LocationRackPickingReturn] Warehouse Code Filter:", warehouseCode); 
        console.log("[LocationRackPickingReturn] Item Code Filter:", itemCode); 
        console.log("[LocationRackPickingReturn] Endpoint URL:", url); 
        console.log("[LocationRackPickingReturn] Raw Rack Data:", rackData); 
        console.log("[LocationRackPickingReturn] Filtered Rack Data:", filteredRackData); 
    }, [warehouse, warehouseCode, itemCode, url, rackData, filteredRackData]); 
    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn('');
    };
    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<RackLocationRecord> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex.replace('_', ' ')}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
                    <Button type="link" size="small" onClick={() => close()}>Close</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) => !!record[dataIndex]?.toString().toLowerCase().includes((value as string).toLowerCase()),
        filterDropdownProps: { onOpenChange: (visible) => { if (visible) setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (
            <Highlighter highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }} searchWords={[searchText]} autoEscape textToHighlight={text ? text.toString() : ''} />
        ) : (text),
    });
    const columns: ColumnsType<RackLocationRecord> = [
        {
            title: 'Location Code',
            dataIndex: 'code_rack',
            key: 'code_rack',
            sorter: (a, b) => (a.code_rack || '').localeCompare(b.code_rack || ''),
            ...getColumnSearchProps('code_rack'),
        }
    ];

    return (
        <div id="iniz" style={transformStyle}>
            <div className="gjs-grid-row" id="it95p">
                <div className="gjs-grid-column" id="itza1">
                    <Table<RackLocationRecord>
                        rowKey="id"
                        columns={columns}
                        dataSource={filteredRackData} 
                        loading={isLoading}
                        bordered
                        size="small"
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                onSelectLocation?.(record);
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default LocationRackPickingReturn;